/**
 * Компоненты таблицы: Table, TableHead, TableBody, TableRow, TableHeader, TableCell
 * Используются для отображения структурированных данных
 */
import type { MouseEvent, ReactNode } from 'react';
import { cn } from '@/utils/cn';

interface TableProps {
  children: ReactNode;
  className?: string;
  /** When false, skip the default scroll/border wrapper (caller provides it). */
  wrapped?: boolean;
}

export function Table({ children, className, wrapped = true }: TableProps) {
  const table = (
    <table className={cn('min-w-full divide-y divide-shell-border', className)}>
      {children}
    </table>
  );

  if (!wrapped) return table;

  return (
    <div className="overflow-x-auto rounded-2xl border border-shell-border bg-shell-surface">
      {table}
    </div>
  );
}

interface TableHeadProps {
  children: ReactNode;
}

export function TableHead({ children }: TableHeadProps) {
  return <thead className="bg-shell-elevated">{children}</thead>;
}

interface TableBodyProps {
  children: ReactNode;
}

export function TableBody({ children }: TableBodyProps) {
  return <tbody className="divide-y divide-shell-border bg-shell-surface">{children}</tbody>;
}

interface TableRowProps {
  children: ReactNode;
  className?: string;
  testId?: string;
  onClick?: () => void;
}

export function TableRow({ children, className, testId, onClick }: TableRowProps) {
  return (
    <tr
      data-testid={testId}
      onClick={onClick}
      className={cn('transition-colors hover:bg-shell-hover', className)}
    >
      {children}
    </tr>
  );
}

interface TableHeaderProps {
  children: ReactNode;
  className?: string;
}

export function TableHeader({ children, className }: TableHeaderProps) {
  return (
    <th
      className={cn(
        'px-6 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-shell-muted',
        className
      )}
    >
      {children}
    </th>
  );
}

interface TableCellProps {
  children: ReactNode;
  className?: string;
  onClick?: (event: MouseEvent<HTMLTableCellElement>) => void;
}

export function TableCell({ children, className, onClick }: TableCellProps) {
  return (
    <td
      onClick={onClick}
      className={cn('px-6 py-4 whitespace-nowrap text-sm text-shell-text', className)}
    >
      {children}
    </td>
  );
}

